import React from "react";
import { ChevronDown, ChevronRight, Database, Zap } from "lucide-react";

import { Chart } from "../../components/charts";
import { barOption, lineOption } from "./chartOptions";

function num(value, digits = 2) {
  if (value === null || value === undefined || value === "") return "--";
  return Number(value).toLocaleString("zh-CN", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function signedNum(value, digits = 1) {
  if (value === null || value === undefined || value === "") return "--";
  const parsed = Number(value);
  return `${parsed >= 0 ? "+" : ""}${num(parsed, digits)}`;
}

function toneClass(value, styles) {
  if (value === null || value === undefined || value === "") return "";
  return Number(value) > 0 ? styles.up : Number(value) < 0 ? styles.down : styles.flat;
}

export function HuijinEtfCard({ data, styles }) {
  const [expanded, setExpanded] = React.useState(false);
  return (
    <section className={styles.card}>
      <div className={styles.sectionTitle}>
        <div>
          <Database size={17} /><strong>汇金 ETF 动向</strong>
          {data && (
            <span className={`${styles.trendBadge} ${styles[data.trend]}`}>
              <Zap size={11} />{data.trend === "accumulating" ? "增持中" : "减持中"}
            </span>
          )}
        </div>
        {data && <span className={styles.cardHeaderTag}>{data.data_date} 收盘 · T+1</span>}
      </div>
      {!data ? (
        <div className={styles.unavailable}><Database size={18} /><span>本地库尚未同步汇金ETF份额/净值数据</span></div>
      ) : (
        <>
          <div className={styles.twoColumn}>
            <div>
              <div className={styles.metricChartHeader}>
                <span className={styles.metricHeaderTitle}>当日净申购<span className={styles.metricTag}>{data.data_date} 收盘</span></span>
                <div className={styles.metricHeaderValues}>
                  <strong className={toneClass(data.net_flow_today_yi, styles)}>
                    {signedNum(data.net_flow_today_yi, 1)} 亿
                  </strong>
                </div>
              </div>
              <small>
                5日净申购{signedNum(data.net_flow_5d_yi, 1)}亿 · 30日净申购{signedNum(data.net_flow_30d_yi, 1)}亿 · 持仓¥{num(data.aum_yi, 0)}亿
              </small>
              {data.net_flow_series.length ? (
                <div className={styles.compactChart}>
                  <Chart option={barOption(data.net_flow_series, "net_flow_yi", "净申购")} />
                </div>
              ) : null}
            </div>
            <div>
              <div className={styles.metricChartHeader}>
                <span className={styles.metricHeaderTitle}>二级市场成交额<span className={styles.metricTag}>{data.data_date} 收盘</span></span>
                <div className={styles.metricHeaderValues}>
                  <strong>{num(data.turnover_yi, 0)} 亿</strong>
                </div>
              </div>
              {data.turnover_change_pct !== null && (
                <small className={toneClass(data.turnover_change_pct, styles)}>
                  较前日{Number(data.turnover_change_pct) >= 0 ? "增加" : "减少"}{num(Math.abs(Number(data.turnover_change_pct)), 2)}%
                </small>
              )}
              {data.turnover_series.length ? (
                <div className={styles.compactChart}>
                  <Chart option={lineOption(data.turnover_series, "turnover_yi", "成交额", "#2563eb", "亿元")} />
                </div>
              ) : null}
            </div>
          </div>

          <small className={styles.trackedIndexListLabel}>按跟踪指数分组 · 近5日净申购</small>
          <div className={styles.trackedIndexList}>
            {data.by_tracked_index.map((item) => (
              <div key={item.tracked_index}>
                <span><strong>{item.tracked_index}</strong></span>
                <span className={toneClass(item.net_flow_5d_yi, styles)}>{signedNum(item.net_flow_5d_yi, 1)} 亿</span>
              </div>
            ))}
          </div>

          <button type="button" className={styles.etfExpandToggle} onClick={() => setExpanded((value) => !value)}>
            {expanded ? "收起" : "展开"} {data.etf_count} 只 ETF 明细 {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
          {expanded && (
            <div className={styles.etfTableScroll}>
              <table className={styles.etfTable}>
                <thead>
                  <tr>
                    <th>ETF 名称</th>
                    <th>跟踪指数</th>
                    <th>现价</th>
                    <th>5D 涨跌</th>
                    <th>5D 净申购</th>
                    <th>规模</th>
                  </tr>
                </thead>
                <tbody>
                  {data.etfs.map((item) => (
                    <tr key={item.ts_code}>
                      <td>{item.name}</td>
                      <td>{item.tracked_index}</td>
                      <td>{num(item.unit_nav, 3)}</td>
                      <td className={toneClass(item.pct_chg_5d, styles)}>
                        {item.pct_chg_5d === null ? "--" : `${signedNum(item.pct_chg_5d, 1)}%`}
                      </td>
                      <td className={toneClass(item.net_flow_5d_yi, styles)}>
                        {signedNum(item.net_flow_5d_yi, 2)}亿
                      </td>
                      <td>{num(item.aum_yi, 0)}亿</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
